/**
 * OOO Functional — Quiz engine (ooo-lp-fnquiz.js)
 *
 * Multi-step flow:
 *   0  intro
 *   1  Q-hardest
 *   2  Q-when
 *   3  Q-feel
 *   4  Q-routine
 *   5  email gate
 *   6  Q-format
 *   result
 *
 * Data attributes used in the Liquid template:
 *   data-ooo-quiz          — root wrapper
 *   data-ooo-quiz-step     — each step element (0..N)
 *   data-ooo-quiz-result   — result panel
 *   data-ooo-start         — intro "Start" button
 *   data-ooo-next          — continue button (per step)
 *   data-ooo-back          — back button (per step)
 *   data-ooo-email-form    — email <form>
 *   data-ooo-email         — email <input>
 *   [data-val]             — answer value on .ooo-fnquiz-opt buttons
 *   [data-stack]           — stack signal on .ooo-fnquiz-opt buttons
 *   data-result-headline   — result headline text node
 *   data-result-name       — result stack name text node
 *   data-result-reason     — result reason text node
 *   data-result-cta        — result CTA anchor (text + href)
 *   data-result-prod-a     — product image A
 *   data-result-prod-b     — product image B
 *
 * localStorage key: "ooo_quiz_answers" (object of {val, stack?} per q)
 */

(function () {
  'use strict';

  var LS_KEY = 'ooo_quiz_answers';

  /* ── result copy map (stack → variant) ──────────────────────── */
  var STACKS = {
    night: {
      headline: "Your brain won't turn off when your body does.",
      name: 'Your Night Protocol',
      reason: 'Because stressful days usually become restless nights.',
      cta: 'Build My Night Routine',
      ctaHref: '/collections/all',
      prodA: { asset: 'ooo-prod-sleep.png', alt: 'Sleep Gummies' },
      prodB: { asset: 'ooo-prod-calm.png',  alt: 'Calm Gummies'  }
    },
    daily: {
      headline: 'The 4pm wave hits you hardest.',
      name: 'Your Daily Stack',
      reason: 'Calm handles the wind-down. Sleep finishes the job at night.',
      cta: 'Start My Protocol',
      ctaHref: '/collections/all',
      prodA: { asset: 'ooo-prod-calm.png',  alt: 'Calm Gummies'  },
      prodB: { asset: 'ooo-prod-sleep.png', alt: 'Sleep Gummies' }
    },
    clarity: {
      headline: 'The noise is louder than the task.',
      name: 'Your Clarity Stack',
      reason: 'Lower the static, the focus comes back. Days feel better when your nervous system isn\'t overloaded.',
      cta: 'Start My Protocol',
      ctaHref: '/collections/all',
      prodA: { asset: 'ooo-prod-focus.png', alt: 'Focus Gummies' },
      prodB: { asset: 'ooo-prod-calm.png',  alt: 'Calm Gummies'  }
    },
    balance: {
      headline: 'You feel less like yourself than you used to.',
      name: 'Your Balance Stack',
      reason: 'Because emotional balance and nervous system support work together.',
      cta: 'Start My Protocol',
      ctaHref: '/collections/all',
      prodA: { asset: 'ooo-prod-mood.png',  alt: 'Mood Gummies'  },
      prodB: { asset: 'ooo-prod-calm.png',  alt: 'Calm Gummies'  }
    }
  };

  /* ── asset base URL injected by Liquid via data attribute ────── */
  function assetUrl(root, filename) {
    var base = root.dataset.oooAssetBase || '';
    if (!base) return '';
    // base ends with the last asset filename; strip it to get the directory
    // Actually Liquid writes it as a full URL per asset; use the pattern
    // stored as JSON map on the root element.
    try {
      var map = JSON.parse(root.dataset.oooAssetMap || '{}');
      return map[filename] || '';
    } catch (e) {
      return '';
    }
  }

  /* ── boot ───────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('[data-ooo-quiz]');
    if (!root) return;

    var steps  = Array.from(root.querySelectorAll('[data-ooo-quiz-step]'));
    var result = root.querySelector('[data-ooo-quiz-result]');
    var answers = {};

    /* restore from localStorage */
    try {
      var saved = localStorage.getItem(LS_KEY);
      if (saved) answers = JSON.parse(saved) || {};
    } catch (e) {}

    var idx = 0;

    /* ── show step ── */
    function show(i) {
      idx = i;
      steps.forEach(function (s, n) {
        s.classList.toggle('is-active', n === i && i < steps.length);
      });
      if (result) {
        result.classList.toggle('is-active', i >= steps.length);
      }
      try {
        window.scrollTo({ top: 0, behavior: 'instant' in window ? 'instant' : 'auto' });
      } catch (e) {
        window.scrollTo(0, 0);
      }
      /* re-trigger reveal on visible step */
      var active = (i < steps.length) ? steps[i] : result;
      if (active) {
        active.querySelectorAll('.ooo-reveal').forEach(function (el) {
          el.classList.add('is-in');
        });
      }
    }

    /* ── answer selection (radio behaviour per step) ── */
    steps.forEach(function (step) {
      var q    = step.dataset.q;
      var opts = step.querySelectorAll('.ooo-fnquiz-opt');
      var nextBtn = step.querySelector('[data-ooo-next]');

      opts.forEach(function (opt) {
        opt.addEventListener('click', function () {
          opts.forEach(function (o) { o.classList.remove('is-selected'); });
          opt.classList.add('is-selected');
          if (q) {
            answers[q] = { val: opt.dataset.val, stack: opt.dataset.stack || null };
            try { localStorage.setItem(LS_KEY, JSON.stringify(answers)); } catch (e) {}
          }
          if (nextBtn) nextBtn.disabled = false;
        });
      });

      /* restore previously-selected answer if returning via Back */
      if (q && answers[q]) {
        opts.forEach(function (opt) {
          if (opt.dataset.val === answers[q].val) {
            opt.classList.add('is-selected');
            if (nextBtn) nextBtn.disabled = false;
          }
        });
      }
    });

    /* ── start ── */
    var startBtn = root.querySelector('[data-ooo-start]');
    if (startBtn) {
      startBtn.addEventListener('click', function () { show(1); });
    }

    /* ── continue ── */
    root.querySelectorAll('[data-ooo-next]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var next = idx + 1;
        if (next < steps.length) {
          show(next);
        } else {
          renderResult();
          show(steps.length);
        }
      });
    });

    /* ── email form ── */
    var emailForm = root.querySelector('[data-ooo-email-form]');
    if (emailForm) {
      emailForm.addEventListener('submit', function (e) {
        e.preventDefault();
        var input = emailForm.querySelector('[data-ooo-email]');
        if (input && input.value) {
          answers.email = input.value;
          try { localStorage.setItem(LS_KEY, JSON.stringify(answers)); } catch (e) {}
        }
        var emailIdx = steps.findIndex(function (s) { return s.dataset.q === 'email'; });
        show(emailIdx + 1);
      });
    }

    /* ── back ── */
    root.querySelectorAll('[data-ooo-back]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (idx > 0) show(idx - 1);
      });
    });

    /* ── result rendering ── */
    function pickStack() {
      if (answers.hardest && answers.hardest.stack) return answers.hardest.stack;
      var votes = {};
      Object.values(answers).forEach(function (a) {
        if (a && a.stack) votes[a.stack] = (votes[a.stack] || 0) + 1;
      });
      var best = 'daily', max = 0;
      Object.entries(votes).forEach(function (kv) {
        if (kv[1] > max) { max = kv[1]; best = kv[0]; }
      });
      return best;
    }

    function renderResult() {
      var s = STACKS[pickStack()] || STACKS.daily;

      var headline = result.querySelector('[data-result-headline]');
      var name     = result.querySelector('[data-result-name]');
      var reason   = result.querySelector('[data-result-reason]');
      var cta      = result.querySelector('[data-result-cta]');
      var prodA    = result.querySelector('[data-result-prod-a]');
      var prodB    = result.querySelector('[data-result-prod-b]');

      if (headline) headline.textContent = s.headline;
      if (name)     name.textContent     = s.name;
      if (reason)   reason.textContent   = s.reason;
      if (cta) {
        cta.textContent = s.cta;
        if (s.ctaHref) cta.href = s.ctaHref;
      }

      /* product images: use data-ooo-asset-* map on result element */
      function setImg(imgEl, assetKey) {
        if (!imgEl) return;
        var mapRaw = result.dataset.oooAssetMap || root.dataset.oooAssetMap || '{}';
        try {
          var map = JSON.parse(mapRaw);
          if (map[assetKey]) imgEl.src = map[assetKey];
        } catch (e) {}
      }
      setImg(prodA, s.prodA.asset);
      if (prodA) prodA.alt = s.prodA.alt;
      setImg(prodB, s.prodB.asset);
      if (prodB) prodB.alt = s.prodB.alt;
    }

    /* ── init (start at intro) ── */
    show(0);
  });
})();
